// src/components/About.js
import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Fade
} from '@mui/material';
import { motion } from 'framer-motion';
import { Email, Instagram, Twitter, Close } from '@mui/icons-material';

import ParticleBackground from '../components/common/ParticleBackground';
import image1 from './assets/images/image1.jpg';
import image2 from './assets/images/image2.jpg';
import image3 from './assets/images/image3.jpeg';

const getGradientByTime = () => {
  const hour = new Date().getHours();
  if (hour >= 7 && hour <= 18) {
    return 'linear-gradient(180deg, rgba(245,245,245,1) 0%, rgba(220,200,190,1) 30%, rgba(255,110,0,0.9) 70%, rgba(20,10,10,1) 100%)';
  } else {
    return 'linear-gradient(180deg, rgba(245,245,245,1) 0%, rgba(180,120,110,1) 30%, rgba(210,60,10,0.95) 70%, rgba(12,3,3,1) 100%)';
  }
};

const About = () => {
  const bgGradient = useMemo(() => getGradientByTime(), []);

  // 🔥 Estado del modal
  const [open, setOpen] = useState(false);

  // 🔥 Estado del form
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    const { name, email, message } = form;

    window.location.href = `mailto:rebelfoxgaming@hotmail.com?subject=Contacto Rebel Fox&body=Nombre: ${name}%0AEmail: ${email}%0AMensaje: ${message}`;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: bgGradient,
        backgroundAttachment: 'fixed',
        overflowX: 'hidden',
      }}
    >
      <ParticleBackground />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 6, md: 10 },
        }}
      >
        {/* Título */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: '#fff',
              textShadow: '0 4px 24px rgba(0,0,0,0.6)',
              mb: 3,
            }}
          >
            ¿Quiénes Somos?
          </Typography>
        </motion.div>

        {/* Texto */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              background: "rgba(10,10,10,0.60)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2,
              p: { xs: 4, md: 7 },
              color: '#fff',
              boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
            }}
          >
            <Box sx={{ color: "#fff" }}>
  <Typography
    variant="h4"
    sx={{
      fontWeight: 700,
      color: "#ff6e00",
      mb: 3,
      textAlign: "center",
    }}
  >
    Nuestra Historia
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.9, fontSize: "1.1rem" }}>
    <strong>Rebel Fox Gaming</strong> es una organización argentina de esports
    creada con una misión clara: demostrar que el talento, la disciplina y la
    pasión pueden construir grandes historias, incluso cuando los recursos son
    limitados.
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.9, fontSize: "1.1rem" }}>
    Nacimos del amor por la competencia y la comunidad gamer. Desde nuestros
    inicios trabajamos para ofrecer a jugadores y equipos un espacio donde
    puedan crecer, aprender y competir en un entorno profesional, fomentando
    valores como el <strong>compromiso</strong>, el{" "}
    <strong>respeto</strong> y el <strong>trabajo en equipo</strong>.
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.9, fontSize: "1.1rem" }}>
    Hoy participamos en diferentes disciplinas competitivas, desarrollamos
    proyectos para impulsar nuevos talentos y trabajamos junto a marcas,
    creadores de contenido y organizaciones que comparten nuestra visión del
    gaming.
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.9, fontSize: "1.1rem" }}>
    En <strong>Rebel Fox Gaming</strong> creemos que un club de esports es mucho
    más que un logo o un equipo: es una comunidad donde cada jugador encuentra
    oportunidades para desarrollarse, cada creador puede potenciar su contenido
    y cada fan forma parte del camino.
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.9, fontSize: "1.1rem" }}>
    Nuestro objetivo es seguir construyendo un ecosistema que genere valor para
    todos sus integrantes, impulsando iniciativas como{" "}
    <strong>academias</strong>, <strong>torneos</strong>,{" "}
    <strong>eventos</strong>, <strong>creación de contenido</strong> y nuevas
    experiencias para la comunidad.
  </Typography>

  <Typography
    variant="h6"
    sx={{
      mt: 5,
      textAlign: "center",
      fontWeight: 700,
      color: "#ffb74d",
      lineHeight: 1.8,
    }}
  >
    Porque para nosotros competir es importante,
    <br />
    pero construir el futuro de los esports es aún más grande.
  </Typography>

  <Typography
    variant="h5"
    sx={{
      mt: 4,
      textAlign: "center",
      fontWeight: 800,
      color: "#ff6e00",
      letterSpacing: 1,
    }}
  >
    Esto recién empieza.
    <br />
    Bienvenido a Rebel Fox Gaming.
  </Typography>
</Box>
           
          </Box>
        </motion.div>

        {/* Galería */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {[image1, image2, image3].map((img, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    width: '100%',
                    height: 220,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                  }}
                >
                  <img
                    src={img}
                    alt={`Foto ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
                <Typography variant="body2" align="center" sx={{ mt: 1, color: '#fff' }}>
                  {i === 0 && 'Equipo de Valorant'}
                  {i === 1 && 'Entrenamiento - Argentina Game Show'}
                  {i === 2 && "Evento 'Fox Kombat'"}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Botón */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Button
            variant="contained"
            color="warning"
            size="large"
            onClick={() => setOpen(true)}
            sx={{
              px: 4,
              boxShadow: '0 0 15px rgba(255,110,0,0.6)',
              '&:hover': {
                boxShadow: '0 0 25px rgba(255,110,0,1)'
              }
            }}
          >
            Contáctanos
          </Button>
        </Box>
      </Container>

      {/* 🔥 MODAL */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Fade}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(10,10,10,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 0 40px rgba(255,110,0,0.4)',
            color: '#fff',
            p: 2,
          }
        }}
      >
        <DialogContent>
          {/* Close */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
              <Close />
            </IconButton>
          </Box>

          {/* Título */}
          <Typography variant="h4" align="center" sx={{ mb: 3 }}>
            Entrá en contacto
          </Typography>

          {/* Links */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 4 }}>
            <IconButton href="mailto:rebelfoxgaming@hotmail.com" sx={{ color: '#ff6e00' }}>
              <Email fontSize="large" />
            </IconButton>

            <IconButton href="https://instagram.com/rebelfoxgaming" target="_blank" sx={{ color: '#ff6e00' }}>
              <Instagram fontSize="large" />
            </IconButton>

            <IconButton href="https://twitter.com/rebelfoxgaming" target="_blank" sx={{ color: '#ff6e00' }}>
              <Twitter fontSize="large" />
            </IconButton>
          </Box>

          {/* Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre"
              name="name"
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ style: { color: '#aaa' } }}
              sx={{ input: { color: '#fff' }, fieldset: { borderColor: '#555' } }}
            />

            <TextField
              label="Email"
              name="email"
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ style: { color: '#aaa' } }}
              sx={{ input: { color: '#fff' }, fieldset: { borderColor: '#555' } }}
            />

            <TextField
              label="Mensaje"
              name="message"
              multiline
              rows={4}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ style: { color: '#aaa' } }}
              sx={{ textarea: { color: '#fff' }, fieldset: { borderColor: '#555' } }}
            />

            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmit}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: '1rem',
                boxShadow: '0 0 15px rgba(255,110,0,0.6)',
                '&:hover': {
                  boxShadow: '0 0 25px rgba(255,110,0,1)'
                }
              }}
            >
              Transmitir mensaje
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default About;